import React, { useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FieldLabel from '../components/FieldLabel';
import { getFieldLabel, matchCountryValue } from '../utils/formHelpers';
import { outlinedFieldProps } from '../utils/fieldStyles';
import { filterGeographyCountriesForContinent } from '../../../../../data/continentCountries';
import {
    COUNTRY_ALREADY_EXISTS_MESSAGE,
    isCountryTaken,
} from '../../wizard/utils/destinationCountry';

/**
 * Destination country picker — same geography source as Add Lead
 * (`useCountries` → `/geography/countries`), optionally filtered by continent.
 */
const CountryField = ({
    field,
    data,
    change,
    hasError,
    errorMessage,
    countries = [],
    takenCountries = [],
}) => {
    const continent = field.filterByContinent ? data.continent : null;
    const availableCountries = useMemo(() => {
        const filtered = filterGeographyCountriesForContinent(
            field.filterByContinent ? continent : null,
            countries
        );
        const current = String(data[field.key] || '').trim();
        if (current && !filtered.some((name) => name.toLowerCase() === current.toLowerCase())) {
            return [current, ...filtered];
        }
        return filtered;
    }, [countries, continent, field.filterByContinent, field.key, data]);

    const selected = matchCountryValue(data[field.key], availableCountries);
    const disabled = field.filterByContinent && !continent;
    const selectedIsTaken = isCountryTaken(selected, takenCountries);
    const showTakenError = hasError || selectedIsTaken;
    const helperText = selectedIsTaken
        ? COUNTRY_ALREADY_EXISTS_MESSAGE
        : (hasError ? (errorMessage || 'This field is required') : '');

    return (
        <Autocomplete
            size="small"
            options={availableCountries}
            value={selected}
            disabled={disabled}
            getOptionDisabled={(option) => isCountryTaken(option, takenCountries)}
            onChange={(_, newValue) => {
                if (newValue && isCountryTaken(newValue, takenCountries)) {
                    return;
                }
                change(field.key, newValue || '');
            }}
            renderOption={(props, option) => {
                const taken = isCountryTaken(option, takenCountries);
                return (
                    <li {...props} key={option}>
                        {option}{taken ? ' (already exists)' : ''}
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...outlinedFieldProps}
                    label={<FieldLabel label={getFieldLabel(field)} tooltip={field.tooltip} />}
                    placeholder={disabled ? 'Select continent first' : 'Select country'}
                    error={showTakenError}
                    helperText={helperText}
                />
            )}
        />
    );
};

export default CountryField;
